// Presentación visual de una línea del episodio. La vista y el texto siguen
// siendo propiedad del runtime; este componente solo organiza su lectura.

import { useEffect, useRef } from "react";

import type { LineView } from "../../engine";
import { TEXTOS_UI } from "../ui/textos-ui";
import { TurnoDeDialogo } from "./TurnoDeDialogo";
import { ladoParaVozUnica, type LadoDeDialogo } from "./lado-dialogo";

interface LineaDeDialogoProps {
  readonly vista: LineView;
  /** Turno inmediatamente anterior, únicamente para el intercambio visual A+B. */
  readonly lineaAnterior: LineView | null;
  readonly permiteRepetir: boolean;
  readonly alContinuar: () => void;
}

function anunciar(region: HTMLElement | null, texto: string): void {
  if (!region) return;
  region.textContent = "";
  requestAnimationFrame(() => {
    region.textContent = texto;
  });
}

function ladoEnIntercambio(
  linea: LineView,
  pareja: LineView,
  ladoAlternativo: LadoDeDialogo,
): LadoDeDialogo {
  if (linea.speaker === "capi") return "izquierda";
  if (pareja.speaker === "capi") return "derecha";

  const indiceVoz = linea.scene.cast.indexOf(linea.speaker);
  const indicePareja = pareja.scene.cast.indexOf(pareja.speaker);
  if (indiceVoz < 0 || indicePareja < 0 || indiceVoz === indicePareja) {
    return ladoAlternativo;
  }
  return indiceVoz < indicePareja ? "izquierda" : "derecha";
}

export function LineaDeDialogo({
  vista,
  lineaAnterior,
  permiteRepetir,
  alContinuar,
}: LineaDeDialogoProps) {
  const regionRef = useRef<HTMLParagraphElement>(null);
  const anuncio = vista.speakerName + ": " + vista.text;
  const intercambioDeDosVoces =
    lineaAnterior !== null &&
    lineaAnterior.scene.id === vista.scene.id &&
    lineaAnterior.speaker !== vista.speaker &&
    vista.scene.cast.length > 1;
  const ladoActual = intercambioDeDosVoces && lineaAnterior
    ? ladoEnIntercambio(vista, lineaAnterior, "derecha")
    : ladoParaVozUnica(vista.speaker);
  // El turno previo solo determina en qué lado queda esta pareja. No se
  // conserva su retrato ni su globo: una persona habla a la vez y «Continuar»
  // sustituye el turno anterior por el siguiente, sin temporizador de lectura.

  useEffect(() => {
    anunciar(regionRef.current, anuncio);
  }, [anuncio]);

  return (
    <div
      className={[
        "dialogo",
        "dialogo--visual",
        "dialogo--una-voz",
        intercambioDeDosVoces ? "dialogo--intercambio" : "",
      ].join(" ")}
      data-lado={ladoActual}
    >
      <div className="dialogo__turnos">
        <TurnoDeDialogo
          key={vista.node.id}
          speaker={vista.speaker}
          nombre={vista.speakerName}
          texto={vista.text}
          lado={ladoActual}
        />
      </div>

      <p
        className="visualmente-oculto"
        aria-live="polite"
        aria-atomic="true"
        ref={regionRef}
      />

      <div className="dialogo__controles">
        {permiteRepetir && !intercambioDeDosVoces ? (
          <button
            type="button"
            className="objetivo-tactil boton boton--secundario dialogo__repetir"
            aria-label={TEXTOS_UI.dialogo.repetir}
            onClick={() => anunciar(regionRef.current, anuncio)}
          >
            Repetir
          </button>
        ) : null}
        <button
          type="button"
          className="objetivo-tactil boton boton--primario dialogo__continuar"
          data-principal="true"
          onClick={alContinuar}
        >
          {TEXTOS_UI.dialogo.continuar}
        </button>
      </div>
    </div>
  );
}
